import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { images } from '../../components/shared/assets';
import { initializeAllFeatures, initializeDragScroll } from '../../utils/interactiveFeatures';
import SmartNavigation from '../../components/Navigation/SmartNavigation';
import Footer from '../../components/Footer/Footer';
import InstagramSection from '../../components/BrandHomePage/InstagramSection';
import { HandDrawnButton } from '../../components/shared/HandDrawnButton/HandDrawnButton';
import { CTALink } from '../../components/shared/CTALink/CTALink';
import { CarouselArrow } from '../../components/shared/CarouselArrow';
import './ProducingNaturePage.css';

const ProducingNaturePage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  // Initialize interactive features
  useEffect(() => {
    // Initialize specific carousels with same ID as brand homepage
    const cleanupMainCarousel = initializeDragScroll('draggable-carousel');
    const cleanupBlogCarousel = initializeDragScroll('producing-nature-blog-carousel');
    
    return () => {
      if (cleanupMainCarousel && typeof cleanupMainCarousel === 'function') cleanupMainCarousel();
      if (cleanupBlogCarousel && typeof cleanupBlogCarousel === 'function') cleanupBlogCarousel();
    };
  }, []);

  return (
    <div className="producing-nature-page">
      {/* Navigation */}
      <SmartNavigation 
        defaultVariant="light"
        scrolledVariant="light"
      />

      {/* Hero Section - Updated to match Figma exactly */}
      <section className="producing-hero" data-scroll-section="hero">
        <h1>
          {t('pages/producingNature:hero.title')}
        </h1>
      </section>

      {/* Parrot Video Section */}
      <section className="parrot-video-section" data-scroll-section="parrot-video">
        <div className="video-container">
          <video
            className="parrot-video"
            autoPlay
            muted
            loop
            playsInline
          >
            <source src={images.rectangle22} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>
      </section>

      {/* Content Carousel Section - Matching Figma MCP exactly */}
      <section 
        className="horizontal-carousel-section" 
        data-scroll-section
      >
        <div 
          className="horizontal-scroll-carousel__scroll-container" 
          id="draggable-carousel"
        >
            {/* Frame 1: Carbon Neutrality - Text left, video right with overlay */}
            <div className="carousel-slide">
              <div className="producing-nature-frame graphic-diptych-1">
            <div className="graphic-text-section">
              <div className="text-bg"></div>
              <div className="header-body">
                <h3>Carbon Neutrality</h3>
                <div className="body-content">
                  <p>We protect 340 hectares of native woodland in Iguazú and Patagonia. These areas absorb over 10,000 tonnes of CO2 per year. This amount is greater than the emissions generated by our three lodges, including the travel emissions of our guests.</p>
                </div>
              </div>
              <div className="drawing-overlay">
                <img src={images.producingNaturePage.carbonNeutralityOverlay} alt="Carbon neutrality design overlay" />
              </div>
            </div>
                <div className="video-section">
                  <img src={images.producingNaturePage.carbonNeutralityBg} alt="Carbon neutrality landscape" />
                </div>
              </div>
            </div>
          
            {/* Frame 2: Image Blurb - Large image top, text content below */}
            <div className="carousel-slide">
              <div className="producing-nature-frame image-blurb">
            <div className="image-blurb-bg"></div>
            <div className="small-image">
              <img src={images.producingNaturePage.forestProtectionImage} alt="Forest protection" />
            </div>
            <div className="main-text">
              <p>We protect 145 hectares of virgin forest in the Atlantic Rainforest near the Iguazu National Park, an incredibly biodiverse area home to a host of native species of flora and fauna and accessible only to Awasi guests. In addition, we own 200 hectares of native woodland in Patagonia.</p>
            </div>
            <div className="bodies-link">
              <p>Awasi Guests can visit these protected areas and take part in the work we do to protect and reintroduce native species to their natural habitats and aid in the conservation of the atlantic rainforest.</p>
              <p>Arcu sed nisl vestibulum feugiat a quam ut eleifend. Sit turpis quam sapien facilisis convallis vivamus eget lectus. Ullamcorper felis rutrum nibh suspendisse nulla non. Adipiscing non etiam imperdiet odio non nec amet habitasse. Sit lobortis egestas urna semper proin auctor. Ut fringilla sagittis dolor lorem sem purus quis eros enim aliquam turpis.</p>
                <CTALink
                  text="read more"
                  href="/blog"
                  className="read-more-text"
                />
              </div>
            </div>
          </div>
          
          {/* Frame 3: Simple Diptych 1 - Split layout with small image */}
          <div className="carousel-slide">
            <div className="producing-nature-frame simple-diptych-1">
            <div className="video-left">
              <img src={images.producingNaturePage.conservationProjectMain} alt="Conservation project" />
            </div>
            <div className="text-block-image">
              <div className="text-block">
                <div className="header-body">
                  <h3>Awasi Patagonia Conservation Project</h3>
                  <p>Since Awasi Patagonia is located right on the edge of the park, we created a natural corridor on our grounds, expanding the protected area creating territories for them to roam freely. We helped restore the natural habitat of native species, and currently between 9 and 12 pumas roam an area that was devoid of wildlife. The Awasi guides help monitor the puma populations and keep hunters away.</p>
                </div>
                <HandDrawnButton
                  text="MEET THE PUMAS"
                  onClick={() => navigate('/patagonia/experiences')}
                  variant="brand"
                  color="awasi"
                  animation="stroke-to-stroke"
                  size="normal"
                  className="link-button"
                />
              </div>
              <div className="small-image-right">
                <img src={images.producingNaturePage.conservationProjectSmall} alt="Small corner image" />
                </div>
              </div>
            </div>
          </div>
          
          {/* Frame 4: Full Video */}
          <div className="carousel-slide">
            <div className="producing-nature-frame full-video">
              <img src={images.producingNaturePage.fullDesertLandscape} alt="Desert landscape" />
            </div>
          </div>

          {/* Frame 5: Simple Diptych 2 - Text left, image right */}
          <div className="carousel-slide">
            <div className="producing-nature-frame simple-diptych-2">
            <div className="description-text-block">
              <div className="header-body">
                <h3>Nature & Wildlife</h3>
                <div className="bodies">
                  <p>Awasi Iguazu is set in one of the last remaining parts of the Atlantic Rainforest, one of the most biodiverse areas of Argentina and one of the best places in the world for bird-watching and butterfly-spotting. Sadly, the rainforest is rapidly decreasing in neighboring areas due to deforestation. At Awasi, we work towards the preservation of this ecosystem through numerous initiatives.</p>
                  <p>Our efforts are focussed on protecting and conserving this valuable ecosystem. We do so by donating and collaborating directly with local entities which work in the protection, rehabilitation and reintroduction of native animals. We also protect natural areas by helping monitor and track local species, and by helping the authorities keep hunters and poachers at bay.</p>
                </div>
              </div>
            </div>
              <div className="image-right">
                <img src={images.producingNaturePage.natureWildlifeImage} alt="Nature and wildlife" />
              </div>
            </div>
          </div>

          {/* Frame 6: Wide Image + Text Block */}
          <div className="carousel-slide">
            <div className="producing-nature-frame wide-image-text">
            <div className="wide-bg"></div>
            <div className="wide-image">
              <img src={images.producingNaturePage.sustainabilityReportImage} alt="Sustainability report" />
            </div>
            <div className="wide-main-text">
              <p>Relais & Châteaux Sustainability Report</p>
            </div>
            <div className="wide-body">
              <p>Relais & Chateaux, the association of 580 hotels and restaurants worldwide (including Awasi Atacama, Patagonia and Iguazu), published its first sustainability report for 2022. With data gathered via operation and consumption surveys taken by all its members, the report examines the environmental and social impact of its member properties and sets 15 goals to meet by 2030.</p>
            </div>
            <div className="wide-link">
                <HandDrawnButton
                  text="READ MORE"
                  onClick={() => navigate('/blog/conservation')}
                  variant="brand"
                  color="awasi"
                  animation="stroke-to-stroke"
                  size="normal"
                  className="link-button"
                />
              </div>
            </div>
          </div>

          {/* Frame 7: Graphic + Text */}
          <div className="carousel-slide">
            <div className="producing-nature-frame graphic-text">
            <div className="text-bg-dark"></div>
            <div className="text-block-final">
              <h3>UN's 17 Goals for Sustainable Development</h3>
              <div className="body-link">
                <p>The United Nations Sustainability Development Goals are blueprint to achieve a better and more sustainable future for all. Education, opportunities, equality and inclusion, applied to hospitality. Read about how Awasi works to meet these standards daily.</p>
                <HandDrawnButton
                  text="READ MORE"
                  onClick={() => navigate('/blog/conservation')}
                  variant="brand"
                  color="off-white"
                  animation="stroke-to-stroke"
                  size="normal"
                  className="link-button white"
                />
              </div>
            </div>
              <div className="drawing-final">
                <img src={images.producingNaturePage.unGoalsOverlay} alt="UN Goals design overlay" />
              </div>
            </div>
          </div>

          {/* Frame 8: Final Video */}
          <div className="carousel-slide">
            <div className="producing-nature-frame final-video">
              <img src={images.producingNaturePage.finalLandscape} alt="Final landscape" />
            </div>
          </div>
        </div>
      </section>

      {/* You Might Enjoy Section */}
      <section className="you-might-enjoy-section" data-scroll-section>
        <div className="you-might-enjoy-header">
          <h2>You might enjoy</h2>
        </div>
        
        {/* Blog Carousel */}
        <div className="blog-carousel-container">
          <div className="blog-carousel" id="producing-nature-blog-carousel">
            {/* Card 1 */}
            <div className="blog-card">
              <div className="blog-card-image">
                <img src={images.producingNaturePage.blogCard1Image} alt="Being inspired by the United Nation's 17 Objectives for Sustainability" />
              </div>
              <div className="blog-card-content">
                <div className="blog-card-header">
                  <h3>Being inspired by the United Nation's 17 Objectives for Sustainability</h3>
                  <div className="blog-card-meta">
                    <span>AWASI IGUAZU</span>
                    <span>-</span>
                    <span>03.02.2022</span>
                  </div>
                </div>
                <p>The Argentine-born artist and photographer employs a unique technique in her work: she applies crumpled images on top of her photographs to highlight the impact man has on nature.</p>
                <div className="blog-card-links">
                  <CTALink
                    text="READ more"
                    href="/blog"
                    className="blog-link"
                  />
                </div>
              </div>
            </div>

            {/* Card 2 */}
            <div className="blog-card">
              <div className="blog-card-image">
                <div className="image-background" style={{ backgroundImage: `url('${images.producingNaturePage.blogCard2Video}')` }}></div>
                <img src={images.producingNaturePage.blogCard2Image} alt="Hospitality in harmony with the natural world" />
              </div>
              <div className="blog-card-content">
                <div className="blog-card-header">
                  <h3>Hospitality in harmony with the natural world</h3>
                  <div className="blog-card-meta">
                    <span>AWASI IGUAZU</span>
                    <span>-</span>
                    <span>03.02.2022</span>
                  </div>
                </div>
                <p>As you watch, as you listen to the beauty of the evening, in that extraordinary quietness when almost everything seems to be motionless, you are then in complete communion, in complete harmony, with everything about you.</p>
                <div className="blog-card-links">
                  <CTALink
                    text="read more"
                    href="/blog"
                    className="blog-link"
                  />
                </div>
              </div>
            </div>

            {/* Card 3 */}
            <div className="blog-card">
              <div className="blog-card-image">
                <img src={images.producingNaturePage.blogCard3Image} alt="Rainer Gross - Double Takes" />
              </div>
              <div className="blog-card-content">
                <div className="blog-card-header">
                  <h3>Rainer Gross - "Double Takes"</h3>
                  <div className="blog-card-meta">
                    <span>AWASI IGUAZU</span>
                    <span>-</span>
                    <span>03.02.2022</span>
                  </div>
                </div>
                <p>Consectetur felis sed eget nisi lacus accumsan suscipit sem. Egestas mattis potenti pretium vulputate lectus ut interdum. Iaculis amet convallis consequat vitae dis commodo. Elementum nibh justo viverra porttitor vulputate vestibulum.</p>
                <div className="blog-card-links">
                  <CTALink
                    text="read more"
                    href="/blog"
                    className="blog-link"
                  />
                </div>
              </div>
            </div>

            {/* Card 4 */}
            <div className="blog-card">
              <div className="blog-card-image">
                <img src={images.producingNaturePage.blogCard4Image} alt="Iliana Díaz López - Portales del Ara Pyau" />
              </div>
              <div className="blog-card-content">
                <div className="blog-card-header">
                  <h3>Iliana Díaz López - "Portales del Ara Pyau"</h3>
                  <div className="blog-card-meta">
                    <span>AWASI IGUAZU</span>
                    <span>-</span>
                    <span>03.02.2022</span>
                  </div>
                </div>
                <p>Consectetur felis sed eget nisi lacus accumsan suscipit sem. Egestas mattis potenti pretium vulputate lectus ut interdum. Iaculis amet convallis consequat vitae dis commodo. Elementum nibh justo viverra porttitor vulputate vestibulum.</p>
                <div className="blog-card-links">
                  <CTALink
                    text="read more"
                    href="/blog"
                    className="blog-link"
                  />
                </div>
              </div>
            </div>
          </div>
          
          {/* Next Arrow */}
          <button
            className="blog-carousel-next"
            onClick={() => {
              const carousel = document.getElementById('producing-nature-blog-carousel');
              if (carousel) {
                const currentTransform = carousel.style.transform || 'translateX(0px)';
                const currentX = currentTransform.includes('translateX') ? 
                  parseFloat(currentTransform.match(/translateX\(([^)]+)\)/)?.[1] || '0') : 0;
                const newX = currentX - 650;
                carousel.style.transform = `translateX(${newX}px)`;
                carousel.style.transition = 'transform 0.3s ease';
                setTimeout(() => {
                  carousel.style.transition = 'none';
                }, 300);
              }
            }}
            aria-label="Next slide"
          >
            <img src="/assets/5a55df86b591cc2829c4f417c7e3bc649cb55d26.svg" alt="Next arrow" />
          </button>
        </div>
      </section>
      
      {/* Instagram Section - Using same configuration as brand homepage */}
      <InstagramSection 
        posts={[
          { src: images.film13, alt: "Instagram 1" },
          { src: images.filmVideo1, alt: "Instagram 2", isVideo: true },
          { src: images.film14, alt: "Instagram 3" },
          { src: images.film15, alt: "Instagram 4" }
        ]}
      />

      {/* Footer */}
      <Footer variant="default" />
    </div>
  );
};

export default ProducingNaturePage;